import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { Eyebrow, AmbientOrbs } from "@/components/common/ui";
import { getArticle, listArticles } from "@/lib/api";

export default function Article() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setArticle(null);
    setNotFound(false);
    Promise.all([getArticle(slug, lang), listArticles(lang)])
      .then(([a, all]) => {
        if (!alive) return;
        setArticle(a);
        setRelated(all.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => alive && setNotFound(true));
    return () => {
      alive = false;
    };
  }, [slug, lang]);

  if (notFound) {
    return (
      <section className="py-40 text-center">
        <p className="text-white/60">Article not found.</p>
        <button onClick={() => navigate(`/${lang}/knowledge`)} className="mt-6 text-bht-gold">← {t("knowledge.back")}</button>
      </section>
    );
  }

  if (!article) return <section className="py-40 text-center text-white/40">Loading…</section>;

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/knowledge/${article.slug}`}
        type="article"
        article={{
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Person", name: article.author },
          datePublished: article.published_at,
          dateModified: article.updated_at,
          articleSection: article.category,
          keywords: article.tags.join(", "),
        }}
      />
      <section className="relative overflow-hidden py-24 lg:py-32">
        <AmbientOrbs />
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Link to={`/${lang}/knowledge`} className="eyebrow inline-flex items-center gap-2 hover:text-bht-gold" data-testid="article-back">
            ← {t("knowledge.back")}
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">{article.category}</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span className="font-mono text-[10px] tracking-widest text-white/50">{article.reading_time} {t("knowledge.reading_time")}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl md:text-6xl"
            data-testid="article-title"
          >
            {article.title}
          </motion.h1>
          <p className="mt-8 text-lg leading-relaxed text-white/70">{article.excerpt}</p>
          <div className="mt-8 flex items-center gap-4 border-y border-white/[0.06] py-4 text-xs text-white/50">
            <span>{article.author}</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span>{new Date(article.published_at).toLocaleDateString(lang)}</span>
          </div>
        </div>

        <div className="mx-auto mt-14 max-w-3xl px-6 lg:px-10">
          <article className="prose prose-invert prose-lg max-w-none font-sans text-white/80 [&_p]:leading-relaxed">
            {article.body.split("\n").map((p, i) => (
              <p key={i} className="mb-6">{p}</p>
            ))}
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-white/[0.06] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <Eyebrow>{t("knowledge.related")}</Eyebrow>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/${lang}/knowledge/${a.slug}`}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/30"
                  data-testid={`article-related-${a.slug}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">{a.category}</span>
                  <div className="mt-4 font-serif text-xl group-hover:text-bht-gold">{a.title}</div>
                  <p className="mt-2 text-sm text-white/55">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
